import { platformBaseUrl } from "@/lib/platformHost";
import {
  renderWaTemplate,
  resolveWaTemplate,
  type WaTemplateOverrides,
} from "@/lib/waTemplates";
// Helper murni Katalog Reseller (mig 216): tiap pelanggan aktif punya link
// katalog publik /toko/<catalog_slug> — tampilan katalog toko ber-identitas
// reseller (white-label), tombol Order membuka WhatsApp reseller. Sengaja BEBAS
// dari Supabase/React supaya gampang diuji (lihat
// src/lib/__tests__/resellerCatalog.test.ts). Cermin gaya shareProduct.ts.

export const RESELLER_CATALOG_BASE = "/toko";

/** Path beranda katalog reseller: /toko/<kode>. */
export function buildCatalogPath(kode: string): string {
  return `${RESELLER_CATALOG_BASE}/${encodeURIComponent(kode.trim())}`;
}

/** Path detail produk dalam mode katalog: /toko/<kode>/product/<slug>. */
export function buildCatalogProductPath(kode: string, slug: string): string {
  return `${buildCatalogPath(kode)}/product/${slug}`;
}

/**
 * True bila `href` menunjuk halaman DI DALAM katalog reseller (`/toko/…`).
 * Dipakai HomeProductCard untuk MENEKAN fallback logo toko pada produk tanpa
 * foto: di dalam katalog, logo toko induk justru yang harus disembunyikan
 * (white-label — lihat metadata /toko/[kode]/*). Diturunkan dari href tujuan
 * kartu, bukan prop terpisah per pemanggil, supaya permukaan katalog baru yang
 * meneruskan href /toko/… otomatis ikut aman.
 */
export function isCatalogHref(href: string | null | undefined): boolean {
  return (
    typeof href === "string" && href.startsWith(`${RESELLER_CATALOG_BASE}/`)
  );
}

/**
 * Path halaman Hasil Pencarian katalog: /toko/<kode>/cari (+ `?q=`/`?brand=`
 * opsional). Search bar beranda katalog = PELUNCUR ke sini (cermin beranda toko
 * → /cari); chip Merek beranda katalog & badge merek detail produk katalog juga
 * menuju ke sini dengan filter merek.
 */
export function buildCatalogSearchPath(
  kode: string,
  params?: { q?: string | null; brand?: string | null; type?: string | null }
): string {
  const search = new URLSearchParams();
  const q = params?.q?.trim();
  if (q) search.set("q", q);
  const brand = params?.brand?.trim();
  if (brand) search.set("brand", brand);
  const type = params?.type?.trim();
  if (type) search.set("type", type);
  const qs = search.toString();
  const base = `${buildCatalogPath(kode)}/cari`;
  return qs ? `${base}?${qs}` : base;
}

/**
 * Path halaman Merek katalog: /toko/<kode>/merek. Tanpa parameter = LANDING
 * daftar semua merek; `brand` = drill-down produk merek itu (cermin /merek
 * toko utama, tetap di dalam katalog reseller). Chip Merek beranda katalog &
 * badge merek detail produk katalog menuju ke sini.
 */
export function buildCatalogMerekPath(
  kode: string,
  params?: { brand?: string | null; type?: string | null }
): string {
  const search = new URLSearchParams();
  const brand = params?.brand?.trim();
  if (brand) search.set("brand", brand);
  const type = params?.type?.trim();
  if (type) search.set("type", type);
  const qs = search.toString();
  const base = `${buildCatalogPath(kode)}/merek`;
  return qs ? `${base}?${qs}` : base;
}

/**
 * Path halaman Kategori katalog: /toko/<kode>/kategori. Tanpa parameter =
 * LANDING grid semua kategori; `cat`/`segment` = drill-down (cermin /kategori
 * toko utama, tetap di dalam katalog reseller).
 */
export function buildCatalogKategoriPath(
  kode: string,
  params?: { cat?: string | null; segment?: string | null; type?: string | null }
): string {
  const search = new URLSearchParams();
  const cat = params?.cat?.trim();
  if (cat) search.set("cat", cat);
  const segment = params?.segment?.trim();
  if (segment) search.set("segment", segment);
  const type = params?.type?.trim();
  if (type) search.set("type", type);
  const qs = search.toString();
  const base = `${buildCatalogPath(kode)}/kategori`;
  return qs ? `${base}?${qs}` : base;
}

/**
 * Path halaman Jelajah katalog: /toko/<kode>/jelajah (+ `?type=` opsional) —
 * feed produk vertikal ala media sosial versi white-label (harga normal,
 * tautan tetap di dalam katalog). Pintu masuknya ikon Jelajah di header
 * beranda katalog, membawa tab tipe yang sedang dibuka.
 */
export function buildCatalogJelajahPath(
  kode: string,
  params?: { type?: string | null }
): string {
  const type = params?.type?.trim();
  const base = `${buildCatalogPath(kode)}/jelajah`;
  return type ? `${base}?type=${encodeURIComponent(type)}` : base;
}

/**
 * Origin tautan katalog yang DIBAGIKAN / DISALIN: apex platform
 * (`https://kelolify.com`), BUKAN `window.location.origin` (mig 8936).
 *
 * Katalog hidup di dua alamat — domain toko (lama, tetap hidup apa adanya) dan
 * `kelolify.com/toko/<kode>`. Yang disebar aplikasi selalu alamat platform:
 * domain toko = nama distributor, persis identitas yang disembunyikan katalog
 * white-label. Satu fungsi supaya tiap tombol bagikan sepakat; ditegakkan
 * tripwire `catalogShareOrigin.test.ts`.
 *
 * Yang SENGAJA tidak memakai ini: tautan internal di dalam katalog (path
 * relatif → tetap di host yang sedang dibuka) dan tombol "Lihat Website Toko
 * Saya" di Akun Saya — reseller dikenali sebagai pemilik lewat sesi login yang
 * hanya ada di domain toko, jadi kunjungannya sendiri tak terhitung "Dilihat".
 */
export function catalogShareOrigin(): string {
  return platformBaseUrl();
}

/**
 * Kode katalog dari path `/toko/<kode>` atau `/toko/<kode>/…` — dipakai
 * middleware apex untuk mengenali katalog di `kelolify.com`. `/toko` polos
 * (DIREKTORI TOKO platform) dan kode berbentuk tak sah → null. Huruf besar
 * dinormalkan, cermin halaman katalog (`kode.trim().toLowerCase()`).
 */
export function catalogKodeFromPath(pathname: string): string | null {
  const match = /^\/toko\/([^/]+)(?:\/.*)?$/.exec(pathname);
  if (!match) return null;
  let kode: string;
  try {
    kode = decodeURIComponent(match[1]).trim().toLowerCase();
  } catch {
    return null;
  }
  return isValidCatalogSlug(kode) ? kode : null;
}

/** Nama pengganti di judul/metadata katalog yang tak tersedia (nonaktif / kode tak dikenal). */
export const CATALOG_NAME_FALLBACK = "Katalog";

/**
 * Judul halaman katalog dari bagian-bagiannya, digabung " · ". Bagian kosong
 * dibuang dan bagian BERURUTAN yang sama dirapatkan, supaya katalog yang tak
 * tersedia berjudul "Katalog", bukan "Katalog · Katalog".
 */
export function catalogTitle(...parts: Array<string | null | undefined>): string {
  const out: string[] = [];
  for (const raw of parts) {
    const part = (raw ?? "").trim();
    if (!part) continue;
    if (out.length > 0 && out[out.length - 1].toLowerCase() === part.toLowerCase()) continue;
    out.push(part);
  }
  return out.join(" · ") || CATALOG_NAME_FALLBACK;
}

/**
 * Sambungan nama reseller sesudah kata "katalog" di deskripsi: " Rima" →
 * "katalog Rima"; nama pengganti → "" sehingga tak lahir "katalog Katalog".
 */
export function catalogNameSuffix(name: string | null | undefined): string {
  const tidy = (name ?? "").trim();
  return tidy && tidy !== CATALOG_NAME_FALLBACK ? ` ${tidy}` : "";
}

/** URL publik beranda katalog dari origin browser (trailing slash dibuang). */
export function buildCatalogUrl(origin: string, kode: string): string {
  const base = (origin ?? "").replace(/\/+$/, "");
  return `${base}${buildCatalogPath(kode)}`;
}

/** URL publik detail produk dalam mode katalog. */
export function buildCatalogProductUrl(
  origin: string,
  kode: string,
  slug: string
): string {
  const base = (origin ?? "").replace(/\/+$/, "");
  return `${base}${buildCatalogProductPath(kode, slug)}`;
}

/**
 * Nama tampilan reseller di katalog — cermin coalesce SQL RPC
 * get_public_reseller_catalog: nama toko, fallback nama lengkap.
 */
export function displayResellerName(
  storeName: string | null | undefined,
  fullName: string
): string {
  const store = storeName?.trim();
  return store && store.length > 0 ? store : fullName.trim();
}

/**
 * Validasi bentuk slug katalog sebelum fetch ke server (guard murah — slug
 * hasil generate_catalog_slug selalu lowercase alnum + strip). Menolak string
 * ngawur/terlalu pendek/terlalu panjang tanpa membebani RPC.
 */
export function isValidCatalogSlug(kode: string): boolean {
  return /^[a-z0-9][a-z0-9-]{2,59}$/.test(kode);
}

export type CatalogOrderInput = {
  /**
   * Nama penerima pesan — sapaan pembuka. Mode katalog: nama toko reseller.
   * Dipakai ulang oleh tombol "Order via WhatsApp" pengunjung TAMU di toko
   * utama (ProductDetail), di sana isinya nama toko.
   */
  resellerName: string;
  /** Nama produk yang di-order. */
  productName: string;
  /** Nama varian terpilih — kosong/null = baris dilewati. */
  variantName?: string | null;
  /** Jumlah pcs (di-clamp minimal 1). */
  qty: number;
  /** Harga siap-tampil (mis. "Rp 250.000") — kosong/null = dilewati (harga menyusul). */
  priceLabel?: string | null;
  /** URL detail produk dalam mode katalog. */
  url: string;
};

/**
 * Susun pesan order WhatsApp ke reseller. Baris yang datanya kosong
 * (varian/harga) otomatis dilewati agar pesan tetap rapi.
 */
export function buildCatalogOrderMessage(
  input: CatalogOrderInput,
  overrides?: WaTemplateOverrides | null
): string {
  const variant = input.variantName?.trim();
  const price = input.priceLabel?.trim();

  return renderWaTemplate(resolveWaTemplate("order_produk", overrides), {
    "[tujuan]": input.resellerName.trim(),
    "[nama produk]": input.productName.trim(),
    "[baris varian]": variant ? `Varian: ${variant}` : "",
    "[jumlah]": String(Math.max(1, Math.floor(input.qty) || 1)),
    "[baris harga]": price ? `Harga: ${price}` : "",
    "[link produk]": input.url.trim()
  });
}

/**
 * Link chat WhatsApp ke nomor reseller (sudah dinormalkan 62xxx via
 * toWaMeNumber oleh pemanggil). Nomor kosong → fallback pemilih kontak
 * (wa.me/?text=) supaya tombol tetap berfungsi.
 */
export function buildCatalogOrderWaUrl(waNumber: string, message: string): string {
  const num = waNumber.trim();
  const text = encodeURIComponent(message);
  return num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
}

/** Teks bagikan link katalog (tombol Bagikan di kartu "Website Toko Saya" Akun). */
export function buildCatalogShareText(
  input: {
    catalogName: string;
    url: string;
  },
  overrides?: WaTemplateOverrides | null
): string {
  return renderWaTemplate(resolveWaTemplate("bagikan_katalog", overrides), {
    "[nama katalog]": input.catalogName.trim(),
    "[link katalog]": input.url.trim()
  });
}

// ─── PESAN ORDER MULTI-PRODUK (keranjang katalog) ───────────────────────────
//
// `buildCatalogOrderMessage` di atas memuat SATU produk dan tetap dipakai
// tombol order cepat di halaman produk + jalur tamu di toko utama. Yang di
// bawah ini melayani keranjang katalog: beberapa baris, ongkir, dan alamat
// dalam satu pesan.
//
// Masukannya sengaja BERBENTUK STRUKTURAL (bukan `CatalogCartLine` yang
// diimpor): modul ini ditarik `ProductDetail`, kartu beranda, dan halaman Akun,
// jadi mengimpor modul keranjang dari sini akan menyeret `biteship.ts` +
// `shipmentWeight.ts` ke bundel halaman yang tak punya keranjang sama sekali.

/**
 * Rupiah untuk TEKS WHATSAPP — sengaja bukan `formatPrice` dari `pricing.ts`.
 *
 * `Intl.NumberFormat(style:"currency")` menyisipkan spasi yang lebarnya
 * bergantung build ICU (spasi biasa vs NBSP U+00A0). Di layar itu tak terlihat;
 * di pesan WhatsApp ia ikut ter-encode ke URL dan membuat keluaran fungsi ini
 * mustahil diuji sebagai string tetap. Pesan order adalah teks yang dikirim ke
 * orang lain — bentuknya harus sama di setiap perangkat.
 */
export function formatCatalogRupiah(amount: number): string {
  const n = Math.round(Number(amount) || 0);
  return `Rp ${n.toLocaleString("id-ID")}`;
}

export type CatalogOrderItem = {
  name: string;
  /** "Merah / XL / Katun" — kosong/null = produk tanpa variasi, segmen dilewati. */
  variantLabel?: string | null;
  qty: number;
  /** `null` = harga belum disetel admin → dicetak "harga menyusul". */
  unitPrice: number | null;
  /** Harga tambahan (mig 7900) yang termasuk di `unitPrice`; absen/0 = tanpa. */
  priceExtra?: number | null;
};

export type CatalogOrderShipping = {
  /** Nama ekspedisi apa adanya, mis. "JNE REG". */
  expedition: string;
  price: number;
};

export type CatalogCartOrderInput = {
  /** Nama toko/reseller — judul pesan. */
  storeName: string;
  items: CatalogOrderItem[];
  /** `null` = pembeli belum mengecek/memilih ongkir. */
  shipping?: CatalogOrderShipping | null;
  /** Blok alamat siap cetak. Kosong = seksi alamat dilewati seluruhnya. */
  address?: string | null;
};

/** Satu baris item: "1. Kaos Polos - Merah / XL - 2 pcs". */
function itemHeadline(index: number, item: CatalogOrderItem, qty: number): string {
  const parts = [item.name.trim()];
  const variant = item.variantLabel?.trim();
  if (variant) parts.push(variant);
  parts.push(`${qty} pcs`);
  return `${index}. ${parts.join(" - ")}`;
}

/**
 * Susun pesan order keranjang katalog.
 *
 * Bentuknya ditetapkan pemilik:
 *
 *     *Format Order <Nama toko>*
 *     1. <produk> - <variasi> - <pcs>
 *        <harga> x <pcs> = <subtotal>
 *
 *     Total : <subtotal> + ongkir <ekspedisi> <ongkir> = <total>
 *
 *     Dikirim ke alamat :
 *     <alamat>
 *
 * Tiga tepi yang ditangani terus terang, bukan disamarkan jadi angka:
 *  • item tanpa harga → "Harga menyusul x N", tak ikut subtotal, dan baris
 *    Total diberi ekor yang menyebut berapa item yang begitu;
 *  • ongkir belum dipilih → "+ ongkir (belum dihitung)" TANPA "= total",
 *    karena total tanpa ongkir adalah angka yang salah;
 *  • alamat kosong → seluruh seksinya dilewati, bukan judul menggantung.
 */
export function buildCatalogCartOrderMessage(
  input: CatalogCartOrderInput,
  overrides?: WaTemplateOverrides | null
): string {
  const rows: string[] = [];
  let subtotal = 0;
  let unpriced = 0;
  let index = 0;

  for (const item of input.items) {
    const qty = Math.max(1, Math.floor(Number(item.qty)) || 1);
    index += 1;
    rows.push(itemHeadline(index, item, qty));

    if (item.unitPrice === null || !Number.isFinite(item.unitPrice)) {
      unpriced += 1;
      rows.push(`   Harga menyusul x ${qty}`);
      continue;
    }
    const lineTotal = item.unitPrice * qty;
    subtotal += lineTotal;
    rows.push(
      `   ${formatCatalogRupiah(item.unitPrice)} x ${qty} = ${formatCatalogRupiah(lineTotal)}`
    );
    // Harga tambahan (mig 7900, R1) — tanpa kalimat "tidak kena diskon".
    if (item.priceExtra != null && item.priceExtra > 0) {
      rows.push(`   (termasuk tambahan ${formatCatalogRupiah(item.priceExtra)}/pcs)`);
    }
  }

  const shipping = input.shipping ?? null;
  const address = input.address?.trim();

  return renderWaTemplate(resolveWaTemplate("katalog_keranjang", overrides), {
    "[nama toko]": input.storeName.trim(),
    "[rincian item]": rows.join("\n"),
    "[baris total]": shipping
      ? `Total : ${formatCatalogRupiah(subtotal)} + ongkir ${shipping.expedition.trim()} ` +
        `${formatCatalogRupiah(shipping.price)} = ${formatCatalogRupiah(subtotal + shipping.price)}`
      : `Total : ${formatCatalogRupiah(subtotal)} + ongkir (belum dihitung)`,
    "[baris harga menyusul]":
      unpriced > 0
        ? `(${unpriced} produk harganya menyusul, belum masuk hitungan)`
        : "",
    "[blok alamat]": address ? `Dikirim ke alamat :\n${address}` : ""
  });
}

/**
 * Blok alamat siap cetak untuk pesan di atas — baris kosong dibuang supaya tak
 * ada baris menggantung saat pembeli melewati salah satu kolom.
 *
 *     Budi Santoso (081234567890)
 *     Jl. Melati No. 12 RT03/RW04
 *     Gubeng, Surabaya, Jawa Timur 60281
 */
export function formatCatalogAddressBlock(input: {
  name: string;
  phone: string;
  detail: string;
  areaLabel: string;
  postalCode?: string;
}): string {
  const name = input.name.trim();
  const phone = input.phone.trim();
  const head = name && phone ? `${name} (${phone})` : name || phone;

  const area = input.areaLabel.trim();
  const postal = (input.postalCode ?? "").trim();
  // Label Biteship kerap SUDAH memuat kode pos ("… DKI Jakarta. 12250").
  // Menempelkannya lagi akan mencetaknya dua kali.
  const areaLine = postal && !area.includes(postal) ? `${area} ${postal}` : area;

  return [head, input.detail.trim(), areaLine].filter((l) => l !== "").join("\n");
}
